import { useMemo } from 'react';
import { assert, CustomError } from '../../errors';
import { Goal } from '../../goals';
import { sortBy } from '../../util/arrays';
import { toDollars } from '../../util/currency';
import { dt } from '../../util/dateTime';
import { useFinancialEntitiesContext } from '../financialEntities';
import { useBaseIdvPosition } from '../idv';
import { getPartnerName, useCurrentUser } from '../user';
import { getGoalAccounts } from './getGoalAccounts';
import {
    getIsJointGoalWaitingForPartnerJointConsent,
    getIsPartnerGoalOnboardingInProgress,
    getIsPartnerGoalPendingJointOpening,
    getIsPartnerGoalWaitingForJointConsent,
    getMyGoalAccountOnboarding,
    getPartnerGoalAccountOnboarding,
    getUpcomingGoalTransactions,
} from './helpers';

export type GoalState =
    | 'SETUP_IN_PROGRESS_V1'
    | 'SETUP_IN_PROGRESS_V2'
    | 'IDV_COMPLETE'
    | 'IDV_INCOMPLETE'
    | 'HAS_INVESTMENTS'
    | 'NO_INVESTMENTS'
    | 'PARTNER_SETTING_UP'
    | 'WAITING_FOR_JOINT_CONSENT'
    | 'WAITING_FOR_PARTNER_JOINT_CONSENT'
    | 'PENDING_JOINT_APPROVAL';

export const useGoalState = (goal: Goal): GoalState => {
    const idvPosition = useBaseIdvPosition({
        justSubmittedIndividualConsent: false,
    });
    const { plentyJointAccountStatus } = useCurrentUser();
    const { fes } = useFinancialEntitiesContext();

    return useMemo(() => {
        const hasInvestments = getUpcomingGoalTransactions({
            goal,
            financialEntities: fes.data?.financialEntities ?? [],
        }).filter((t) => t.isMine).length;

        // TODO: Figure out more retirement goal states
        if (goal.type === 'RETIREMENT') {
            return hasInvestments ? 'HAS_INVESTMENTS' : 'NO_INVESTMENTS';
        }

        if (
            plentyJointAccountStatus &&
            getIsPartnerGoalWaitingForJointConsent(
                goal,
                plentyJointAccountStatus
            )
        ) {
            // Current user's partner is waiting for the current user to open a joint account
            return 'WAITING_FOR_JOINT_CONSENT';
        }

        if (
            plentyJointAccountStatus &&
            getIsPartnerGoalPendingJointOpening(goal, plentyJointAccountStatus)
        ) {
            // Joint account has been sent to Atomic and is now pending approval
            return 'PENDING_JOINT_APPROVAL';
        }

        if (
            plentyJointAccountStatus &&
            getIsJointGoalWaitingForPartnerJointConsent(
                goal,
                plentyJointAccountStatus
            )
        ) {
            // Current user has created a joint goal and is waiting for their partner to open a joint account
            return 'WAITING_FOR_PARTNER_JOINT_CONSENT';
        }

        if (getIsPartnerGoalOnboardingInProgress(goal)) {
            // Partner is setting up this shared goal
            return 'PARTNER_SETTING_UP';
        }

        if (getGoalAccounts(goal).length === 0) {
            const isConfirmed =
                !!getMyGoalAccountOnboarding(goal)?.confirmedAt ||
                (goal.accountStructure === 'JOINT' &&
                    !!getPartnerGoalAccountOnboarding(goal)?.confirmedAt);

            if (!isConfirmed) {
                return goal.onboardingVersion === 'v1'
                    ? 'SETUP_IN_PROGRESS_V1'
                    : 'SETUP_IN_PROGRESS_V2';
            }

            return idvPosition === 'complete'
                ? 'IDV_COMPLETE'
                : 'IDV_INCOMPLETE';
        }

        return hasInvestments ? 'HAS_INVESTMENTS' : 'NO_INVESTMENTS';
    }, [goal, idvPosition, plentyJointAccountStatus, fes]);
};

export const useGoalSubtitle = ({
    goal,
    platform,
}: {
    goal: Goal;
    platform: 'web' | 'mobile';
}): string => {
    const { partnerInfo } = useCurrentUser();
    const goalState = useGoalState(goal);
    const { fes } = useFinancialEntitiesContext();

    switch (goalState) {
        // For now, v1 goals are only on web, and v2 goals are only on mobile
        case 'SETUP_IN_PROGRESS_V1':
            return platform === 'web'
                ? 'Goal setup in progress'
                : 'Continue setup on web';
        case 'SETUP_IN_PROGRESS_V2':
            return platform === 'web'
                ? 'Continue setup on mobile'
                : 'Goal setup in progress';
        case 'IDV_COMPLETE':
            return 'Identity verification in progress';
        case 'IDV_INCOMPLETE':
            return 'Finish opening account';
        case 'HAS_INVESTMENTS': {
            const upcomingTxns = getUpcomingGoalTransactions({
                goal,
                financialEntities: fes.data?.financialEntities ?? [],
            }).filter((t) => t.isMine);
            const nextTxn = sortBy(upcomingTxns, (t) => t.date, 'DESC').at(0);

            assert(nextTxn, 'Expected nextTxn');

            return `${toDollars(nextTxn.amount, {
                alwaysSigned: true,
                withCents: true,
            })} ${dt(nextTxn.date).fromNowDays()}`;
        }
        case 'NO_INVESTMENTS':
            return 'No scheduled investments';
        case 'PARTNER_SETTING_UP':
            return `${getPartnerName(partnerInfo)} is setting up this shared goal`;
        case 'WAITING_FOR_JOINT_CONSENT':
            return `Finish opening your Joint account to join ${getPartnerName(partnerInfo)}`;
        case 'WAITING_FOR_PARTNER_JOINT_CONSENT':
            return `Waiting for ${getPartnerName(partnerInfo)} to open their Joint account`;
        case 'PENDING_JOINT_APPROVAL':
            return 'Pending Joint account approval';
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = goalState;
            throw new CustomError('Unknown goal state', {
                state: goalState,
            });
        }
    }
};
